// Session progress-state derivation — single source of truth for the
// patient-portal frontend.
//
// The backend ships SessionSummary with `scheduledAt`, `durationMinutes`,
// `joinWindowEndsAt`, `canJoinNow`, and `status`. This helper folds those
// into one of six progress states the UI keys off of for visual treatment
// (border tone, "Live now" / "Just ended" markers) and gating decisions
// (when to keep a session in the upcoming list vs flip it to past).
//
// The state space mirrors the /v/* public-link JWT validity window
// (startTime - 30 min through endTime + 180 min) — same window the backend
// now uses for the in-app join CTA. See PatientSessionService doc-strings.

import { parseUtcDate } from './dates'

// In-window thresholds, in minutes. Mirrors backend:
//   PatientSessionService.JOIN_WINDOW_BEFORE_START_MIN = 30
//   PatientSessionService.JOIN_WINDOW_AFTER_END_MIN    = 180
// Kept here as fallback so the frontend never produces a state stale
// relative to the backend window, even if a slightly older payload arrives.
const STARTING_SOON_LEAD_MIN = 30
const RECENT_GRACE_MIN = 180

/**
 * Compute the progress state of a session.
 *
 * @param {object} session — SessionSummary-shaped object with at least
 *   {scheduledAt, durationMinutes, status, joinWindowEndsAt?, canJoinNow?}
 * @returns {('UPCOMING'|'STARTING_SOON'|'IN_PROGRESS'|'RECENTLY_ENDED'|'PAST'|'CANCELLED'|'NONE')}
 */
export function getSessionProgressState(session) {
  if (!session) return 'NONE'
  const status = (session.status || '').toUpperCase()
  if (status === 'CANCELLED' || status === 'NO_SHOW') return 'CANCELLED'

  const startMs = session.scheduledAt
    ? parseUtcDate(session.scheduledAt).getTime()
    : null
  if (startMs == null) return 'UPCOMING'

  const durationMs = (session.durationMinutes || 0) * 60_000
  const endMs = startMs + durationMs
  // Trust backend's joinWindowEndsAt when present (it factors in the exact
  // grace constant); else compute the same way locally.
  const windowEndMs = session.joinWindowEndsAt
    ? parseUtcDate(session.joinWindowEndsAt).getTime()
    : endMs + RECENT_GRACE_MIN * 60_000

  const now = Date.now()
  if (now > windowEndMs) return 'PAST'
  if (now > endMs) return 'RECENTLY_ENDED'
  if (now >= startMs) return 'IN_PROGRESS'
  if ((startMs - now) <= STARTING_SOON_LEAD_MIN * 60_000) return 'STARTING_SOON'
  return 'UPCOMING'
}

/**
 * Convenience predicate: true when the session is happening right now or has
 * just ended (still inside the post-end grace). Drives the "ongoing" visual
 * treatment shared by SessionCard / NextSessionCard / LiveNowCard.
 */
export function isOngoing(session) {
  const s = getSessionProgressState(session)
  return s === 'IN_PROGRESS' || s === 'RECENTLY_ENDED'
}

/**
 * Short human-readable label for the state — surfaced as the colored pill
 * on session cards. Returns null for states that don't need a marker.
 */
export function progressMarkerLabel(state) {
  switch (state) {
    case 'IN_PROGRESS':
      return 'Live now'
    case 'RECENTLY_ENDED':
      return 'Just ended'
    case 'STARTING_SOON':
      return 'Starting soon'
    default:
      return null
  }
}

/**
 * Theme tone for visual emphasis: 'success' for live (sage), 'primary' for
 * starting-soon (ink-blue), 'muted' for recently-ended, null for plain.
 */
export function progressMarkerTone(state) {
  switch (state) {
    case 'IN_PROGRESS':
      return 'success'
    case 'STARTING_SOON':
      return 'primary'
    case 'RECENTLY_ENDED':
      return 'muted'
    default:
      return null
  }
}
